import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import StarRating from './StarRating';
import { toast } from 'sonner';

const ReviewForm = ({ 
  productId, 
  existingReview = null, 
  onSubmit, 
  onCancel 
}) => {
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [comment, setComment] = useState(existingReview?.comment || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (existingReview) {
      setRating(existingReview.rating);
      setComment(existingReview.comment || '');
    }
  }, [existingReview]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    if (comment.length > 1000) {
      toast.error('Comment must be less than 1000 characters');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit({ rating, comment });
      // Reset form if it's a new review (not editing)
      if (!existingReview) {
        setRating(0);
        setComment('');
      }
    } catch (error) {
      console.error('Failed to submit review:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="glass-effect p-6 rounded-xl" data-testid="review-form">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold">
          {existingReview ? 'Edit Your Review' : 'Write a Review'}
        </h3>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition"
            data-testid="cancel-review-btn"
          >
            <X size={24} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Rating Selection */}
        <div>
          <label className="block text-sm font-semibold mb-2">
            Your Rating <span className="text-red-500">*</span>
          </label>
          <StarRating 
            rating={rating} 
            onRatingChange={setRating} 
            size={32}
            readonly={false}
          />
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm font-semibold mb-2">
            Your Review (Optional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your thoughts about this product..."
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500 min-h-[120px]"
            maxLength={1000}
            data-testid="review-comment-input"
          />
          <p className="text-xs text-gray-400 mt-1">
            {comment.length}/1000 characters
          </p>
        </div>

        {/* Submit Button */}
        <div className="flex space-x-4">
          <button
            type="submit"
            disabled={isSubmitting || rating === 0}
            className="flex-1 btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-review-btn"
          >
            {isSubmitting ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 btn-secondary py-3"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;